/**
 * CaseWorkspacePage — chat-first case workspace.
 *
 * Route: /cases/:id/workspace
 *
 * Layout:
 *   Desktop (lg+):  top status bar │ central chat │ right sidebar
 *   Mobile:         top status bar │ chat (full width) │ bottom-sheet drawer
 *
 * Data sources: all existing endpoints — no new backend calls.
 * The wizard remains accessible via "Edit form" link.
 */
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cases, documents } from "@/lib/endpoints";
import { AppHeader } from "@/components/AppHeader";
import { WorkspaceChatPanel, type WorkspaceGreeting } from "@/components/workspace/WorkspaceChatPanel";
import {
  GATING_QUESTIONS,
  INITIAL_GATING_STATE,
  type GatingState,
} from "@/components/workspace/ChatGatingFlow";
import {
  type PendingExtraction,
  type ConfirmedExtraction,
  DOC_TYPE_LABELS,
  MOCK_FIELDS,
} from "@/components/workspace/ChatUploadZone";
import { type NBA } from "@/components/workspace/GuidancePromptCard";
import { SECTION_TO_STEP } from "@/lib/caseConstants";
import { CaseStatusSidebar } from "@/components/workspace/CaseStatusSidebar";
import { MobileStatusDrawer } from "@/components/workspace/MobileStatusDrawer";
import { cn } from "@/lib/utils";
import type { GateRequest } from "@/lib/api-types";
import type { ChatMessageItem } from "@/lib/endpoints";

export function CaseWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [gatingState, setGatingState] = useState<GatingState>(INITIAL_GATING_STATE);
  // ── T4: document upload flow ─────────────────────────────────────────────
  const [docUploadQueue, setDocUploadQueue] = useState<string[]>([]);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [pendingExtraction, setPendingExtraction] = useState<PendingExtraction | null>(null);
  const [confirmedExtractions, setConfirmedExtractions] = useState<ConfirmedExtraction[]>([]);
  const [uploadDocError, setUploadDocError] = useState<string | null>(null);
  // T7: show NBA guidance card in chat after the upload flow completes.
  const [showNbaCard, setShowNbaCard] = useState(false);

  // Derived: the document type currently waiting for upload (null = no upload pending)
  const uploadPromptDoc = pendingExtraction === null ? (docUploadQueue[currentDocIndex] ?? null) : null;

  const queryClient = useQueryClient();

  // ── Data ────────────────────────────────────────────────────────────────────
  const caseQ = useQuery({
    queryKey: ["case", id],
    queryFn: () => cases.get(id!),
    enabled: !!id,
    staleTime: 0,
  });

  const evalQ = useQuery({
    queryKey: ["case", id, "evaluation"],
    queryFn: () => cases.evaluation(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const jobsQ = useQuery({
    queryKey: ["case", id, "jobs"],
    queryFn: () => cases.listJobs(id!),
    enabled: !!id,
    staleTime: 30_000,
    // Poll while a job is actively running so the PDF card updates automatically.
    refetchInterval: (query) => {
      const hasRunning = query.state.data?.items?.some(
        (j) => j.state === "pending" || j.state === "running",
      );
      return hasRunning ? 5_000 : false;
    },
  });

  const docsQ = useQuery({
    queryKey: ["case", id, "documents"],
    queryFn: () => documents.list(id!),
    enabled: !!id,
    staleTime: 60_000,
  });

  // ── Shared helper — must be defined before mutations that call it ───────────
  // Injects a session-only assistant message into the chat cache.
  // Not stored in DB; disappears on hard refresh.
  function injectChatMessage(content: string) {
    const msg: ChatMessageItem = {
      id: `sys-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      role: "assistant",
      content,
      created_at: new Date().toISOString(),
    };
    queryClient.setQueryData<{ items: ChatMessageItem[]; total: number; available: boolean }>(
      ["chat", id],
      (prev) => ({
        items: [...(prev?.items ?? []), msg],
        total: (prev?.total ?? 0) + 1,
        available: prev?.available ?? true,
      }),
    );
  }

  // ── T3: gate mutation (before early returns — Rules of Hooks) ──────────────
  const gateMutation = useMutation({
    mutationFn: (answers: GateRequest) => cases.gate(id!, answers),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["case", id] });
      queryClient.invalidateQueries({ queryKey: ["cases"] });

      const summaryText =
        result.eligibility_status === "eligible"
          ? "✓ Great news — you appear eligible for naturalization! Now let's collect your supporting documents. I'll walk you through each one."
          : result.eligibility_status === "out_of_scope"
            ? buildOosMessage(result.disqualifying_question)
            : "Your answers have been noted. Some items may need closer review — I've flagged them in the status panel. An immigration attorney can help clarify your situation.";

      injectChatMessage(summaryText);
      setGatingState((prev) => ({ ...prev, active: false, submitting: false }));

      // T4: kick off document collection for eligible cases.
      if (result.eligibility_status === "eligible") {
        const queue = ["green_card", "passport_photo"];
        if (variables.married_to_usc === true) queue.push("marriage_certificate");
        setDocUploadQueue(queue);
        setCurrentDocIndex(0);
      }
    },
    onError: (err) => {
      setGatingState((prev) => ({
        ...prev,
        submitting: false,
        error: err instanceof Error ? err.message : "Something went wrong. Please try again.",
      }));
    },
  });

  // ── T4: document upload mutation (before early returns — Rules of Hooks) ───
  const uploadDocMutation = useMutation({
    mutationFn: ({ file, docType }: { file: File; docType: string }) =>
      documents.upload(id!, file, docType),
    onSuccess: (doc, variables) => {
      queryClient.invalidateQueries({ queryKey: ["case", id, "documents"] });
      const resolvedType = doc.document_type ?? variables.docType;
      const mockFields = MOCK_FIELDS[resolvedType] ?? [];
      if (mockFields.length > 0) {
        setPendingExtraction({
          documentId: doc.id,
          documentType: resolvedType,
          fileName: doc.file_name_original ?? "Document",
        });
      } else {
        injectChatMessage(
          `✓ Got it — your ${DOC_TYPE_LABELS[resolvedType] ?? resolvedType} has been saved.`,
        );
        advanceDocQueue();
      }
    },
    onError: (err) => {
      setUploadDocError(
        err instanceof Error ? err.message : "Upload failed. Please try again.",
      );
    },
  });

  // ── Loading / error states ──────────────────────────────────────────────────
  if (caseQ.isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (caseQ.isError || !caseQ.data) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-3">
        <p className="text-slate-500 text-sm">Could not load this case.</p>
        <Link to="/dashboard" className="text-blue-600 text-sm underline">
          Back to dashboard
        </Link>
      </div>
    );
  }

  // ── Derived state ───────────────────────────────────────────────────────────
  const c = caseQ.data;
  const eval_ = evalQ.data;
  const isPaid = !!c.paid_at;
  const isReady = eval_?.ready ?? false;
  const missingCount = eval_?.missing_data?.length ?? 0;
  const feeDollars = c.fee_total_cents ? (c.fee_total_cents / 100).toFixed(0) : "49";

  // Rough completion %. 20 is a safe cap for N-400 required fields.
  const TOTAL_REQUIRED = 20;
  const progressPct = evalQ.isLoading
    ? null
    : isReady
      ? 100
      : Math.round((Math.max(0, TOTAL_REQUIRED - missingCount) / TOTAL_REQUIRED) * 100);

  const jobs = jobsQ.data?.items ?? [];
  const latestDoneJob = jobs.find((j) => j.state === "done");

  // ── T3: gating handlers ─────────────────────────────────────────────────────
  function handleStartGating() {
    setGatingState({ ...INITIAL_GATING_STATE, active: true });
  }

  function handleGatingAnswer(field: keyof GateRequest, value: boolean) {
    const newAnswers = { ...gatingState.answers, [field]: value };
    const newStep = gatingState.step + 1;

    if (newStep < GATING_QUESTIONS.length) {
      // More questions remain — advance step.
      setGatingState((prev) => ({ ...prev, step: newStep, answers: newAnswers }));
    } else {
      // All 8 answered — fire the gate API.
      setGatingState((prev) => ({ ...prev, step: newStep, answers: newAnswers, submitting: true }));
      gateMutation.mutate(newAnswers as GateRequest);
    }
  }

  function handleGatingRetry() {
    setGatingState((prev) => ({ ...prev, error: null, submitting: true }));
    gateMutation.mutate(gatingState.answers as GateRequest);
  }

  // ── T4: document upload handlers ────────────────────────────────────────────

  /** Advance to the next document in the upload queue (or close the queue). */
  function advanceDocQueue() {
    setUploadDocError(null);
    const nextIndex = currentDocIndex + 1;
    if (nextIndex < docUploadQueue.length) {
      setCurrentDocIndex(nextIndex);
    } else {
      // Queue exhausted — close it and surface the NBA guidance card.
      setDocUploadQueue([]);
      setCurrentDocIndex(0);
      injectChatMessage(
        "All set with documents for now. Here's what to focus on next:",
      );
      setShowNbaCard(true);
    }
  }

  /** User dropped/selected a file for the current document prompt. */
  function handleDocFile(file: File) {
    setUploadDocError(null);
    uploadDocMutation.mutate({ file, docType: uploadPromptDoc! });
  }

  /** User tapped "Looks correct →" on the extraction review card. */
  function handleExtractionConfirm() {
    if (!pendingExtraction) return;
    setConfirmedExtractions((prev) => [
      ...prev,
      {
        ...pendingExtraction,
        fields: MOCK_FIELDS[pendingExtraction.documentType] ?? [],
      },
    ]);
    injectChatMessage(
      `✓ Saved — I've noted the details from your ${DOC_TYPE_LABELS[pendingExtraction.documentType] ?? pendingExtraction.documentType}.`,
    );
    setPendingExtraction(null);
    advanceDocQueue();
  }

  /** User tapped "Skip for now" on the extraction review card. */
  function handleExtractionSkip() {
    if (!pendingExtraction) return;
    injectChatMessage(
      `No problem — your ${DOC_TYPE_LABELS[pendingExtraction.documentType] ?? pendingExtraction.documentType} is saved. You can review the details in the form editor.`,
    );
    setPendingExtraction(null);
    advanceDocQueue();
  }

  /** User tapped "Skip for now →" on the upload zone (skips uploading entirely). */
  function handleDocSkip() {
    injectChatMessage(
      `No problem — you can upload your ${DOC_TYPE_LABELS[uploadPromptDoc!] ?? uploadPromptDoc} later from the documents section.`,
    );
    advanceDocQueue();
  }

  // ── Workspace greeting (T2 + T3) ────────────────────────────────────────────
  // Computed from existing data — no AI call, not stored in DB.
  // Only shown when chat history is empty (WorkspaceChatPanel handles the guard).
  // evalQ loading is allowed to settle before we show a definitive greeting.
  // For new cases, the CTA now triggers handleStartGating instead of navigating.
  const greeting: WorkspaceGreeting | undefined = evalQ.isLoading
    ? undefined
    : computeGreeting({
        id: id!, c, isReady, isPaid, missingCount, progressPct,
        hasPdf: !!latestDoneJob,
        onStartGating: handleStartGating,
      });

  // Until chat-gating writes eligibility_status="eligible", the wizard
  // isn't usable — every form-mapping rule assumes the case is screened
  // in. Both top-bar buttons here ("Edit form" and the primary action)
  // point at /wizard, so we gate them. WizardLayout itself has a
  // redirect-back guard for direct URL navigation.
  const wizardOpen = c.eligibility_status === "eligible";
  const wizardDisabledTitle = "Finish the eligibility check before editing the form.";

  // Top-bar primary action
  const primaryHref = latestDoneJob
    ? `/cases/${id}`                          // download from CaseDetailPage
    : isPaid
      ? `/cases/${id}/wizard/review`          // generate PDF
      : `/cases/${id}/wizard/checkout`;       // pay
  const primaryLabel = latestDoneJob
    ? "Download PDF →"
    : isPaid
      ? "Generate packet →"
      : `Pay $${feeDollars} →`;
  const primaryStyle = latestDoneJob
    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
    : isPaid
      ? "bg-blue-600 hover:bg-blue-700 text-white"
      : "bg-slate-900 hover:bg-slate-800 text-white";

  // Sidebar props (shared between desktop sidebar and mobile drawer)
  const sidebarProps = {
    caseId: id!,
    caseData: c,
    evalData: eval_,
    evalLoading: evalQ.isLoading,
    jobsData: jobs,
    docsData: docsQ.data?.items ?? [],
    feeDollars,
    confirmedExtractions,
  };

  // T7: NBA computed at render time from existing data — pure, no I/O.
  const nba: NBA | null = showNbaCard
    ? computeNBA({
        caseId: id!,
        latestDoneJob,
        isPaid,
        isReady,
        missingData: eval_?.missing_data ?? [],
        warnings: eval_?.warnings ?? [],
        feeDollars,
        eligibilityStatus: c.eligibility_status,
      })
    : null;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <AppHeader />

      {/* ── Workspace status bar ─────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200/80 px-4 sm:px-6 lg:px-8 py-3 sticky top-16 z-30">
        <div className="max-w-[1400px] mx-auto flex items-center gap-3 sm:gap-4">

          {/* Back to dashboard */}
          <Link
            to="/dashboard"
            className="shrink-0 text-xs text-slate-400 hover:text-blue-600 flex items-center gap-1 transition-colors"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">Dashboard</span>
          </Link>

          <div className="hidden sm:block h-4 w-px bg-slate-200 shrink-0" />

          {/* Case name */}
          <span className="text-sm font-semibold text-slate-900 shrink-0 hidden sm:inline">Form N-400</span>

          {/* Progress bar */}
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden min-w-0">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-700",
                  isReady ? "bg-emerald-500" : "bg-blue-500",
                )}
                style={{ width: `${progressPct ?? 0}%` }}
              />
            </div>
            <span className="text-xs font-medium text-slate-500 shrink-0 tabular-nums w-9 text-right">
              {progressPct !== null ? `${progressPct}%` : ""}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Mobile: open status drawer */}
            <button
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Status
            </button>

            {/* Edit form (wizard) — disabled until eligibility passes */}
            {wizardOpen ? (
              <Link
                to={`/cases/${id}/wizard`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span className="hidden sm:inline">Edit form</span>
              </Link>
            ) : (
              <span
                title={wizardDisabledTitle}
                aria-disabled="true"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 bg-slate-100 px-3 py-1.5 rounded-lg cursor-not-allowed"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span className="hidden sm:inline">Edit form</span>
              </span>
            )}

            {/* Primary action — hidden pre-eligibility (every target path
                lives under /wizard, which redirects back here anyway). */}
            {wizardOpen && (
            <Link
              to={primaryHref}
              className={cn(
                "inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors",
                primaryStyle,
              )}
            >
              {primaryLabel}
            </Link>
            )}
          </div>
        </div>
      </div>

      {/* ── Main content ─────────────────────────────────────────────────────── */}
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6 items-start">

          {/* Central chat area */}
          <div className="flex-1 min-w-0">
            <WorkspaceChatPanel
              caseId={id!}
              greeting={greeting}
              gatingActive={gatingState.active}
              gatingState={gatingState}
              onGatingAnswer={handleGatingAnswer}
              onGatingRetry={handleGatingRetry}
              uploadPromptDoc={uploadPromptDoc}
              pendingExtraction={pendingExtraction}
              uploadingDoc={uploadDocMutation.isPending}
              uploadError={uploadDocError}
              confirmedExtractions={confirmedExtractions}
              onDocFile={handleDocFile}
              onExtractionConfirm={handleExtractionConfirm}
              onExtractionSkip={handleExtractionSkip}
              onDocSkip={handleDocSkip}
              nba={nba}
            />
          </div>

          {/* Right status sidebar — desktop only */}
          <aside className="hidden lg:block w-80 xl:w-96 shrink-0">
            <div className="sticky top-[7.5rem] max-h-[calc(100dvh-8.5rem)] overflow-y-auto pb-6 pr-1">
              <CaseStatusSidebar {...sidebarProps} />
            </div>
          </aside>
        </div>
      </main>

      {/* ── Mobile status drawer ─────────────────────────────────────────────── */}
      <MobileStatusDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <CaseStatusSidebar {...sidebarProps} />
      </MobileStatusDrawer>
    </div>
  );
}

// ─── Greeting computation ─────────────────────────────────────────────────────
//
// Pure function — no side effects, no API calls.
// Returns undefined while eval data is loading so the greeting doesn't flash
// an incorrect state before data arrives.

function computeGreeting({
  id,
  c,
  isReady,
  isPaid,
  missingCount,
  progressPct,
  hasPdf,
  onStartGating,
}: {
  id: string;
  c: import("@/lib/api-types").CaseResponse;
  isReady: boolean;
  isPaid: boolean;
  missingCount: number;
  progressPct: number | null;
  hasPdf: boolean;
  /** When provided, the new-case CTA triggers in-chat gating instead of navigating. */
  onStartGating?: () => void;
}): WorkspaceGreeting {

  // ── PDF already generated ────────────────────────────────────────────────
  if (hasPdf) {
    return {
      text: "Your N-400 draft is ready to download. Review it carefully before filing — make sure every answer is accurate. Remember: USCIS filing fees are separate and paid directly to USCIS.",
      cta: { label: "Download PDF", href: `/cases/${id}` },
    };
  }

  // ── Paid, waiting to generate ────────────────────────────────────────────
  if (isPaid) {
    return isReady
      ? {
          text: "Your fee is paid and your application is complete. Head to the review step to generate your N-400 draft.",
          cta: { label: "Generate draft →", href: `/cases/${id}/wizard/review` },
        }
      : {
          text: `Your fee is paid — great. ${missingCount} required field${missingCount !== 1 ? "s" : ""} still need attention before you can generate your draft. I can point you to the right sections.`,
          cta: { label: "Continue application →", href: `/cases/${id}/wizard` },
        };
  }

  // ── Eligible, in progress ────────────────────────────────────────────────
  if (c.eligibility_status === "eligible") {
    return isReady
      ? {
          text: "Welcome back! Your N-400 is complete. When you're ready, pay once to unlock and download your formatted draft.",
          cta: { label: "Pay to unlock →", href: `/cases/${id}/wizard/checkout` },
        }
      : {
          text: `Welcome back! Your N-400 is ${progressPct ?? 0}% complete — ${missingCount} required field${missingCount !== 1 ? "s" : ""} still needed. I can guide you through what's left, or you can jump straight into the form.`,
          cta: { label: "Continue in form →", href: `/cases/${id}/wizard` },
        };
  }

  // ── Out of scope ─────────────────────────────────────────────────────────
  if (c.eligibility_status === "out_of_scope") {
    return {
      text: "Based on your earlier answers, your case has factors that need attorney review before we can prepare your N-400. We'll let you know if our coverage expands to include your situation.",
    };
  }

  // ── Manual review ────────────────────────────────────────────────────────
  if (c.eligibility_status === "manual_review") {
    return {
      text: "Your eligibility check flagged some items for closer review. An immigration attorney should look at your situation before you file. In the meantime, I can help you understand what was flagged.",
    };
  }

  // ── New case — no eligibility check yet ──────────────────────────────────
  return {
    text: "Hi! I'm your N-400 assistant. Before we dive in, let's quickly confirm you qualify for naturalization — it only takes a couple of minutes.\n\nOnce that's done, I'll guide you through collecting your documents and completing your application step by step.",
    cta: onStartGating
      // Preferred: trigger in-chat gating flow (T3)
      ? { label: "Check my eligibility", onCtaClick: onStartGating }
      // Fallback: navigate to standalone gating page (if handler unavailable)
      : { label: "Check my eligibility", href: `/cases/${id}/gating` },
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Builds a plain-English OOS explanation from the disqualifying question key. */
function buildOosMessage(disqualifyingQuestion?: string): string {
  const reasons: Record<string, string> = {
    has_criminal_history:
      "you have a criminal history that requires attorney review before filing",
    has_prior_uscis_denial:
      "USCIS has previously denied a naturalization application from you",
    has_removal_proceedings:
      "you have been in removal or deportation proceedings",
    continuous_us_residence:
      "your U.S. residence history doesn't meet the continuous residence requirement",
    meets_lpr_duration:
      "you haven't been a Permanent Resident for the required number of years yet",
    is_self_filing:
      "our tool is designed for self-filers — a representative-filed application needs different preparation",
    currently_in_us:
      "you're not currently living in the United States",
  };

  const reason = disqualifyingQuestion ? reasons[disqualifyingQuestion] : null;
  const base = reason
    ? `Based on your answers, we can't prepare your N-400 right now — ${reason}.`
    : "Based on your answers, your situation has factors that need attorney review before we can prepare your N-400.";

  return `${base}\n\nYour case information is saved and nothing has been deleted. We recommend consulting a licensed immigration attorney for guidance specific to your situation.`;
}

// ─── NBA computation (T7) ─────────────────────────────────────────────────────
//
// Pure function — no side effects, no API calls.
// Priority: pdf_ready → paid_no_pdf → ready_unpaid → missing_fields → risk_flags

function computeNBA({
  caseId,
  latestDoneJob,
  isPaid,
  isReady,
  missingData,
  warnings,
  feeDollars,
  eligibilityStatus,
}: {
  caseId: string;
  latestDoneJob: import("@/lib/api-types").JobResponse | undefined;
  isPaid: boolean;
  isReady: boolean;
  missingData: import("@/lib/api-types").EvaluationIssue[];
  warnings: import("@/lib/api-types").EvaluationIssue[];
  feeDollars: string;
  eligibilityStatus?: string | null;
}): NBA | null {
  // NBA only makes sense once eligibility is confirmed.
  if (eligibilityStatus !== "eligible") return null;

  if (latestDoneJob) {
    return { type: "pdf_ready", casePath: `/cases/${caseId}` };
  }

  if (isPaid) {
    return {
      type: "paid_no_pdf",
      isReady,
      reviewPath: isReady
        ? `/cases/${caseId}/wizard/review`
        : `/cases/${caseId}/wizard`,
    };
  }

  if (isReady) {
    return {
      type: "ready_unpaid",
      feeDollars,
      checkoutPath: `/cases/${caseId}/wizard/checkout`,
    };
  }

  if (missingData.length > 0) {
    const items = missingData.slice(0, 3).map((issue) => ({
      message: issue.message,
      wizardPath: `/cases/${caseId}/wizard/${
        (issue.section && SECTION_TO_STEP[issue.section]) ?? "personal"
      }`,
    }));
    return { type: "missing_fields", items };
  }

  if (warnings.length > 0) {
    return { type: "risk_flags" };
  }

  return null;
}
